import type { OrganizationTheme } from '../api/types'
import { DEFAULT_BRAND } from './applyTheme'
import { COLOR_TOKENS, FONT_PROPERTIES, RADIUS_TOKENS, SPACING_TOKENS, TOKEN_PREFIX } from './tokens'

/*
  Tracer's own brand, as a theme document.

  WHY THIS EXISTS WHEN "NO THEME" ALREADY MEANS TRACER

  Because a branding screen full of empty boxes is not a starting point. An admin opening
  it sees thirty blank fields and no indication of what any of them currently is, and the
  first useful thing they can do — take the product's palette and move it two steps toward
  their own — is the one thing the form cannot express.

  So this builds the document the product would write if it were describing itself, and
  the editor offers it as a preset. Loading it changes nothing on screen: every value in
  it is the value already being rendered. What it changes is that the values are now IN
  the form, where they can be edited.

  THE VALUES ARE NOT WRITTEN HERE

  They are read from the stylesheet, through the defaults the theme provider captured
  before it wrote anything. There are already three places the token contract lives
  (brand.css, tokens.ts, ThemeTokens.cs) and a fourth copy of the palette — one that only
  the preset uses, and so the one nobody would notice going stale — is exactly the kind of
  drift the contract test exists to prevent.

  Loading and saving this produces a stored theme that is byte-for-byte the product's own
  brand. That is not the same as having no theme, and the difference is deliberate: an
  organization with no theme follows Tracer if Tracer's palette ever changes, and one that
  saved this preset does not. The editor says so.
*/

/**
 * The "Resolve" mark as a standalone asset.
 *
 * Not what the preset puts in `logo.markUrl` — see the note on the preset below. It is
 * here for the admin who wants the Tracer mark as an explicit image (a customer keeping
 * the mark and changing only the name, most likely), and for anywhere the mark is needed
 * outside a React tree.
 *
 * Concrete hexes rather than tokens, because an `<img>` renders in its own document and
 * inherits nothing from this one. That is also why it is the worse choice for the preset.
 *
 * Proportions match `TracerLogo`: widths 1.5 / 2.5 / 4 / 6 / 12, every gap exactly 2.
 * They are load-bearing — change one and change the others to match.
 */
const TRACER_MARK_SVG = [
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" role="img" aria-label="Tracer">`,
  `<rect x="3" y="16.5" width="1.5" height="7" rx="0.75" fill="#0B3556"/>`,
  `<rect x="6.5" y="16.5" width="2.5" height="7" rx="1.25" fill="#0B3556"/>`,
  `<rect x="11" y="16.5" width="4" height="7" rx="1.5" fill="#0B3556"/>`,
  `<rect x="17" y="16.5" width="6" height="7" rx="1.5" fill="#0B3556"/>`,
  `<rect x="25" y="16.5" width="12" height="7" rx="1.5" fill="#1872A7"/>`,
  `</svg>`,
].join('')

/**
 * The mark as a `data:` URI, in the form the API accepts and the branding editor stores.
 *
 * Percent-encoded rather than base64: it stays legible in the settings row and in the
 * form field, and it is a third smaller.
 */
export const TRACER_MARK_DATA_URI = `data:image/svg+xml,${encodeURIComponent(TRACER_MARK_SVG)}`

/**
 * The square app icon, for the browser tab. Primary tile, mark reversed out of it.
 *
 * A separate asset because the wide mark is unreadable at 16px — the fine leading bars
 * turn to mud, which is the whole reason `TracerLogo` has a compact variant.
 */
const TRACER_ICON_SVG = [
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">`,
  `<rect width="64" height="64" rx="14" fill="#0B3556"/>`,
  `<g transform="translate(6.4 6.4) scale(1.28)">`,
  `<rect x="3" y="16.5" width="1.5" height="7" rx="0.75" fill="#FFFFFF"/>`,
  `<rect x="6.5" y="16.5" width="2.5" height="7" rx="1.25" fill="#FFFFFF"/>`,
  `<rect x="11" y="16.5" width="4" height="7" rx="1.5" fill="#FFFFFF"/>`,
  `<rect x="17" y="16.5" width="6" height="7" rx="1.5" fill="#FFFFFF"/>`,
  `<rect x="25" y="16.5" width="12" height="7" rx="1.5" fill="#3498CE"/>`,
  `</g></svg>`,
].join('')

export const TRACER_ICON_DATA_URI = `data:image/svg+xml,${encodeURIComponent(TRACER_ICON_SVG)}`

/**
 * The product's own brand as an explicit theme document.
 *
 * @param defaults The stylesheet's values, from `getDefaultTokens()`. Anything missing is
 *   omitted rather than guessed — a token absent from the document means the stylesheet
 *   does not declare it, and inventing a value would put a colour in the form that
 *   nothing on screen is actually using.
 *
 * NO LOGO, DELIBERATELY.
 *
 * `logo.markUrl` is left unset so the built-in vector mark renders instead, and that is
 * the better artefact in three ways an `<img>` cannot match: it recolours with the
 * palette, it drops to the three-element compact form below 24px, and it reverses cleanly
 * on a dark fill. An admin who wants the mark as a fixed image can paste
 * {@link TRACER_MARK_DATA_URI}, and the editor offers exactly that — but making it the
 * default would hand every preset user a worse mark than the one they already had.
 */
export function tracerTheme(defaults: Record<string, string>): OrganizationTheme {
  const colors = collect(COLOR_TOKENS, TOKEN_PREFIX.colors)
  const radii = collect(RADIUS_TOKENS, TOKEN_PREFIX.radii)
  const spacing = collect(SPACING_TOKENS, TOKEN_PREFIX.spacing)

  const fonts: Record<string, string> = {}
  for (const [key, property] of Object.entries(FONT_PROPERTIES)) {
    const value = defaults[property]?.trim()
    if (value) fonts[key] = value
  }

  return {
    productName: DEFAULT_BRAND.productName,
    tagline: DEFAULT_BRAND.tagline,
    ...(Object.keys(colors).length > 0 ? { colors } : {}),
    ...(Object.keys(radii).length > 0 ? { radii } : {}),
    ...(Object.keys(spacing).length > 0 ? { spacing } : {}),
    ...(Object.keys(fonts).length > 0 ? { fonts } : {}),
  }

  function collect(tokens: readonly string[], prefix: string): Record<string, string> {
    const values: Record<string, string> = {}

    for (const token of tokens) {
      const value = defaults[`${prefix}${token}`]?.trim()
      if (value) values[token] = value
    }

    return values
  }
}
